import { useEffect, useState, type CSSProperties } from "react";
import { COULEURS, POLICE_UI_SPLASH } from "./splashStyles.js";

/**
 * ============================================================================
 *  InvitePlan.tsx — « Afficher le plan du domaine », jusqu'à ce qu'on l'ait fait
 * ----------------------------------------------------------------------------
 *  Sur ordinateur, l'étiquette repliée du coin bas gauche dit « où suis-je » ;
 *  rien n'y dit qu'elle s'ouvre sur le plan. Une bulle flotte au-dessus d'elle
 *  pour l'annoncer, aux couleurs du panneau qu'elle ouvre.
 *
 *  Elle disparaît à la PREMIÈRE ouverture du plan et ne revient plus : le
 *  visiteur a compris, l'insister serait du bruit. Le choix est gardé dans le
 *  navigateur (`localStorage`) ; si le stockage est refusé, il vaut pour la page.
 *
 *  Elle est posée DANS le bouton de l'étiquette : un clic sur la bulle ouvre le
 *  plan aussi, et elle s'efface avec l'étiquette à l'ouverture comme pendant le
 *  titre d'arrivée. Le bouton porte déjà son nom accessible : la bulle est
 *  muette pour les lecteurs d'écran.
 * ==========================================================================*/

const CLE = "saint-sixt.plan-decouvert";

function dejaDecouvert(): boolean {
  try {
    return window.localStorage.getItem(CLE) === "1";
  } catch {
    return false;
  }
}

function retenirDecouvert(): void {
  try {
    window.localStorage.setItem(CLE, "1");
  } catch {
    // Stockage refusé : la bulle ne revient pas dans cette page, rien de plus.
  }
}

export function InvitePlan({ texte, ouvert }: { texte: string; ouvert: boolean }) {
  const [decouvert, setDecouvert] = useState(dejaDecouvert);

  useEffect(() => {
    if (!ouvert || decouvert) return;
    retenirDecouvert();
    setDecouvert(true);
  }, [ouvert, decouvert]);

  if (decouvert) return null;

  return (
    <span aria-hidden className="sn-invite" style={bulle}>
      <span style={losange} />
      {texte}
      <span style={pointe} />
    </span>
  );
}

/* --- styles : ceux de l'étiquette et du panneau (`SceneNav`) --------------- */

const FOND = "rgba(16,26,23,.86)";
const BORD = "1px solid rgba(246,238,227,.22)";

const bulle: CSSProperties = {
  position: "absolute",
  // Bord à bord avec l'étiquette : -1 px rattrape sa bordure, que le
  // positionnement absolu laisse hors du bloc conteneur.
  left: -1,
  right: -1,
  bottom: "calc(100% + 12px)",
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 12px",
  whiteSpace: "nowrap",
  background: FOND,
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  border: BORD,
  borderRadius: 2,
  // L'ombre et sa lueur sont animées (`sn-invite-lueur`, sceneNav.css).
  color: "#E3D9C8",
  fontFamily: POLICE_UI_SPLASH,
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: ".16em",
  textTransform: "uppercase",
};

const losange: CSSProperties = {
  width: 6,
  height: 6,
  transform: "rotate(45deg)",
  background: COULEURS.sauge,
  flex: "none",
};

/** La pointe vers l'étiquette : un carré tourné, bordé sur ses deux côtés bas. */
const pointe: CSSProperties = {
  position: "absolute",
  left: 28,
  bottom: -5,
  width: 8,
  height: 8,
  transform: "rotate(45deg)",
  background: "#17221e",
  borderRight: BORD,
  borderBottom: BORD,
};
